import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input, Select, Textarea } from '@/components/ui/Field';
import { Banner, EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { FileUpload } from '@/components/ui/FileUpload';
import { PageHeader } from '@/app/PlaceholderPage';
import { usePeriod } from '@/app/PeriodSwitcher';
import { databaseApi } from '@/features/database/api';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { EVIDENCE_CATEGORIES, monevApi, type Reflection, type ReflectionInput } from './api';

/**
 * L-09b — Refleksi PER PROGRAM.
 *
 * Satu TBM yang menjalankan tiga program mengisi tiga refleksi, bukan satu
 * yang menggabungkan ketiganya. Itu bentuk lembar aslinya, dan memang lebih
 * masuk akal: capaian dan kendala tiap program berbeda-beda.
 */
export function ReflectionsPage() {
  const queryClient = useQueryClient();
  const { periods, current, currentId, isAll } = usePeriod();

  const [editingId, setEditingId] = useState<string | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Reflection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const activePeriodId =
    current?.id ?? periods.find((p) => p.status === 'active')?.id ?? periods[0]?.id ?? '';

  const { data, isLoading, error: loadError, refetch } = useQuery({
    queryKey: ['reflections', currentId],
    queryFn: () => monevApi.listReflections(isAll ? undefined : (current?.id ?? undefined)),
    enabled: true,
  });

  const remove = useMutation({
    mutationFn: () => monevApi.deleteReflection(deleting!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reflections'] });
      setDeleting(null);
      setBanner('Refleksi dihapus.');
    },
    onError: (err) => {
      setDeleting(null);
      setError(err instanceof ApiError ? err.message : 'Gagal menghapus refleksi.');
    },
  });

  const rows = data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Refleksi Program"
        description={
          isAll
            ? 'Satu refleksi untuk tiap program · Semua Semester'
            : `Satu refleksi untuk tiap program · ${current?.name ?? '—'}`
        }
        action={<Button onClick={() => setEditingId(null)}>+ Refleksi Baru</Button>}
      />

      {banner && <Banner tone="success">{banner}</Banner>}
      {error && editingId === undefined && <Banner tone="danger">{error}</Banner>}

      {loadError ? (
        <ErrorState message={(loadError as Error).message} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : rows.length === 0 ? (
        <Card padded={false}>
          <EmptyState
            icon="📔"
            title="Belum ada refleksi di periode ini"
            description="Catat satu refleksi untuk setiap program yang dijalankan TBM."
          />
        </Card>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {rows.map((row) => (
            <Card key={row.id} className="flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-ink-secondary">
                    {row.tbmCode}{row.tbmName ? ` — ${row.tbmName}` : ''}
                  </p>
                  <h3 className="mt-0.5 text-[17px] font-semibold text-ink leading-snug">{row.programName}</h3>
                </div>
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2.5 py-0.5 text-[12px] font-medium',
                    row.status === 'final'
                      ? 'bg-success-soft text-success'
                      : 'bg-warn-soft text-[#9a6400]',
                  )}
                >
                  {row.status === 'final' ? 'Final' : 'Draf'}
                </span>
              </div>

              <p className="mt-1.5 text-[13px] text-ink-secondary">
                Pengamatan: <span className="tabular font-medium text-ink">{row.observedDate ?? 'Belum ada tanggal'}</span>
              </p>

              <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                <Button size="sm" variant="secondary" onClick={() => setEditingId(row.id)}>
                  Buka Refleksi
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-danger hover:bg-danger-soft hover:text-danger cursor-pointer"
                  onClick={() => setDeleting(row)}
                >
                  Hapus
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {editingId !== undefined && (
        <ReflectionEditor
          reflectionId={editingId}
          periodId={activePeriodId}
          onClose={() => {
            setEditingId(undefined);
            setError(null);
          }}
          onSaved={(message) => {
            queryClient.invalidateQueries({ queryKey: ['reflections'] });
            setBanner(message);
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Hapus refleksi?"
        confirmLabel="Ya, hapus"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
      >
        <p className="mb-3 rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 font-medium text-ink">
          {deleting?.programName}
        </p>
        <p>Berkas bukti yang sudah diunggah tidak ikut terhapus, tapi jadi tidak tertaut ke mana pun.</p>
      </ConfirmDialog>
    </div>
  );
}

const QUESTIONS = [
  { key: 'purpose', n: 2, label: 'Apa tujuan atau perubahan yang ingin dicapai?' },
  { key: 'achievements', n: 3, label: 'Apa saja capaian dari program ini?' },
  { key: 'challenges', n: 5, label: 'Apa tantangan atau kendala yang masih dihadapi?' },
  { key: 'rootCause', n: 6, label: 'Menurut Anda, mengapa tantangan itu terjadi?' },
  { key: 'nextActions', n: 7, label: 'Apa yang akan dilakukan setelah monev ini?' },
  { key: 'lessons', n: 8, label: 'Jika mengulang, apa yang dipertahankan dan apa yang diperbaiki?' },
] as const;

function ReflectionEditor({
  reflectionId,
  periodId,
  onClose,
  onSaved,
}: {
  reflectionId: string | null;
  periodId: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [form, setForm] = useState<Record<string, string>>({});
  const [tbmId, setTbmId] = useState('');
  const [savedId, setSavedId] = useState<string | null>(reflectionId);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { data: tbmList } = useQuery({
    queryKey: ['tbm', 'all'],
    queryFn: () => databaseApi.listTbm({ page: 1 }),
  });

  const { data: existing } = useQuery({
    queryKey: ['reflection', reflectionId],
    queryFn: () => monevApi.getReflection(reflectionId!),
    enabled: Boolean(reflectionId),
  });

  useEffect(() => {
    if (!existing) return;
    setTbmId(existing.tbmId);
    setForm({
      observedDate: existing.observedDate ?? '',
      programName: existing.programName,
      purpose: existing.purpose ?? '',
      achievements: existing.achievements ?? '',
      challenges: existing.challenges ?? '',
      rootCause: existing.rootCause ?? '',
      nextActions: existing.nextActions ?? '',
      lessons: existing.lessons ?? '',
    });
  }, [existing]);

  const set = (key: string, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFieldErrors((e) => ({ ...e, [key]: '' }));
  };

  const save = useMutation({
    mutationFn: (status: 'draft' | 'final') => {
      const input: ReflectionInput = {
        tbmId,
        periodId,
        observedDate: form.observedDate || null,
        programName: (form.programName ?? '').trim(),
        purpose: form.purpose || null,
        achievements: form.achievements || null,
        challenges: form.challenges || null,
        rootCause: form.rootCause || null,
        nextActions: form.nextActions || null,
        lessons: form.lessons || null,
        status,
      };
      return savedId
        ? monevApi.updateReflection(savedId, input).then(() => ({ id: savedId }))
        : monevApi.createReflection(input);
    },
    onSuccess: (res, status) => {
      // Id disimpan supaya kotak unggah bukti langsung bisa dipakai tanpa
      // menutup dan membuka ulang — berkas butuh id entitas untuk ditautkan.
      setSavedId(res.id);
      setError(null);
      onSaved(status === 'final' ? 'Refleksi difinalkan.' : 'Refleksi tersimpan sebagai draf.');
      if (status === 'final') onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Gagal menyimpan refleksi.'),
  });

  function attempt(status: 'draft' | 'final') {
    const errors: Record<string, string> = {};
    if (!tbmId) errors.tbmId = 'TBM wajib dipilih';
    if (!(form.programName ?? '').trim()) errors.programName = 'Nama program wajib diisi';
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    save.mutate(status);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-[var(--radius-card)] bg-surface shadow-[var(--shadow-hover)]">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-[19px] font-semibold text-ink">
            {reflectionId ? 'Refleksi Program' : 'Refleksi Baru'}
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {error && (
            <div className="mb-4">
              <Banner tone="danger">{error}</Banner>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="TBM"
              value={tbmId}
              onChange={(e) => setTbmId(e.target.value)}
              error={fieldErrors.tbmId}
              required
            >
              <option value="">Pilih TBM</option>
              {(tbmList?.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} — {t.name}
                </option>
              ))}
            </Select>
            <Input
              label="Tanggal pengamatan"
              type="date"
              value={form.observedDate ?? ''}
              onChange={(e) => set('observedDate', e.target.value)}
            />
          </div>

          <div className="mt-4">
            <Input
              label="1 · Program apa yang dilaksanakan periode ini?"
              value={form.programName ?? ''}
              onChange={(e) => set('programName', e.target.value)}
              error={fieldErrors.programName}
              required
            />
          </div>

          {QUESTIONS.slice(0, 2).map((q) => (
            <div key={q.key} className="mt-4">
              <Textarea
                label={`${q.n} · ${q.label}`}
                value={form[q.key] ?? ''}
                onChange={(e) => set(q.key, e.target.value)}
              />
            </div>
          ))}

          <h3 className="mt-6 mb-1 text-[17px] font-semibold text-ink">
            4 · Bukti bahwa capaian itu benar terjadi
          </h3>
          {savedId ? (
            <div className="grid gap-4 sm:grid-cols-3">
              {EVIDENCE_CATEGORIES.map((cat) => (
                <div key={cat.key} className="rounded-[var(--radius-input)] bg-app p-3.5">
                  <FileUpload
                    entityType={cat.key}
                    entityId={savedId}
                    label={cat.label}
                    hint="JPG, PNG, WebP, atau PDF"
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 text-[13px] text-ink-secondary">
              Simpan sebagai draf dulu — berkas bukti butuh refleksi yang sudah tersimpan untuk
              ditautkan.
            </p>
          )}

          {QUESTIONS.slice(2).map((q) => (
            <div key={q.key} className="mt-4">
              <Textarea
                label={`${q.n} · ${q.label}`}
                value={form[q.key] ?? ''}
                onChange={(e) => set(q.key, e.target.value)}
              />
            </div>
          ))}
        </div>

        <div className="flex flex-col-reverse gap-2.5 border-t border-border px-5 py-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
            Tutup
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => attempt('draft')}
            loading={save.isPending}
          >
            Simpan Draf
          </Button>
          <Button type="button" onClick={() => attempt('final')} loading={save.isPending}>
            Finalkan
          </Button>
        </div>
      </div>
    </div>
  );
}
